import { z } from 'zod';
import { refersTo } from '../common/refers-to';
import { DealPhaseSchema, DealStatusSchema } from './deals.schemas';

/**
 * Where an Auftrag's Objekt stands in Vermarktung (IMM-1152) — derived at read
 * time by the deal module, never stored. Precedence, first match wins:
 * SOLD (a Kaufinteressent WON, or archived as SOLD) → RENTED / ARCHIVED (archived)
 * → INACTIVE → RESERVED (ACTIVE, a Kaufinteressent in Reserviert / Notartermin)
 * → ONLINE (ACTIVE, a live portal listing) → NOT_ONLINE.
 */
export const MANDATE_PROPERTY_STATES = [
  'NOT_ONLINE',
  'ONLINE',
  'RESERVED',
  'SOLD',
  'RENTED',
  'INACTIVE',
  'ARCHIVED',
] as const;
export const MandatePropertyStateSchema = z.enum(MANDATE_PROPERTY_STATES);
export type MandatePropertyState = z.infer<typeof MandatePropertyStateSchema>;

const MandateListItemObject = z.object({
  dealId: z.uuid(),
  organizationId: refersTo('organization', z.uuid()),
  propertyId: refersTo('property', z.uuid()),
  // Both null when the Objekt cannot be read in the deal's Standort; the row still renders.
  propertyTitle: z.string().nullable(),
  propertyAddress: z.string().nullable(),
  phase: DealPhaseSchema,
  // A LOST deal keeps the phase it was lost in as `phase`, as on the Deal-Überblick.
  status: DealStatusSchema,
  propertyState: MandatePropertyStateSchema.nullable(),
  // When the deal FIRST reached MANDATE_RECEIVED or a later phase — the list's sort key.
  mandateReceivedAt: z.iso.datetime(),
  responsibleMemberIds: z.array(refersTo('member', z.uuid())),
  responsibleMemberNames: z.array(z.string()),
});
export type MandateListItem = z.infer<typeof MandateListItemObject>;
export const MandateListItemSchema: z.ZodType<MandateListItem> = MandateListItemObject;

export const ListMandatesPathParamsSchema = z.object({ workspaceId: z.uuid() });
// The Standort switcher's narrow. It only ever intersects the visible set.
export const ListMandatesQuerySchema = z.object({ organizationId: z.uuid().optional() });

const ListMandatesResponseObject = z.object({
  items: z.array(MandateListItemSchema),
  truncated: z.boolean(),
});
export type ListMandatesResponse = z.infer<typeof ListMandatesResponseObject>;
export const ListMandatesResponseSchema: z.ZodType<ListMandatesResponse> = ListMandatesResponseObject;

/** The bound on the scan, as on the Deal-Überblick: a safety net, surfaced as `truncated`. */
export const MANDATE_LIST_MAX_ROWS = 2000;
